import React, { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../lib/supabase';

interface CSVImportProps {
  type: 'students' | 'families' | 'assignments';
  onImportComplete: () => void;
}

export const CSVImport: React.FC<CSVImportProps> = ({ type, onImportComplete }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string>('');

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus('Parsing CSV...');

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        try {
          await importData(results.data);
          setImportStatus(`Successfully imported ${results.data.length} ${type}!`);
          onImportComplete();
        } catch (error) {
          console.error('Import error:', error);
          setImportStatus(`Error importing: ${JSON.stringify(error, null, 2)}`);
        } finally {
          setIsImporting(false);
        }
      },
      error: (error) => {
        setImportStatus(`Error parsing CSV: ${error.message}`);
        setIsImporting(false);
      }
    });
  };

  const importData = async (data: any[]) => {
    if (type === 'students') {
      await importStudents(data);
    } else if (type === 'families') {
      await importFamilies(data);
    } else {
      await importAssignments(data);
    }
  };

  const importStudents = async (data: any[]) => {
    const students = data.map((row: any) => ({
      student_id: parseInt(row.studentId) || null,
      family_name: row.familyName || '',
      first_name: row.firstName || '',
      combined_name: row.studentCombinedName || `${row.firstName} ${row.familyName}`,
      address: row.address || null,
      phone_number: row.phoneNumber || null,
      home_country: row.homeCountry || null,
      email: row.email || '',
      gender: row.gender || null,
      marital_status: row.maritalStatus || null,
      major: row.major || null,
      program_level: row.programLevel || null,
      special_interests: row.specialInterests || null,
      has_dietary_restrictions: row.hasDietaryRestrictions === 'Yes',
      dietary_restrictions_details: row.dietaryRestrictionsDetails || null,
      willing_to_share: row.willingToShare === 'Yes',
      vaccination_status: row.vaccinationStatus || null,
      semester_year: row.semesterYear || null,
      time_submitted: row.timeSubmitted ? new Date(row.timeSubmitted).toISOString() : null
    }));

    setImportStatus(`Importing ${students.length} students to database...`);
    const { error } = await supabase.from('students').insert(students);
    if (error) {
      console.error('Supabase error:', error);
      throw new Error(error.message || 'Database error');
    }
  };

  const importFamilies = async (data: any[]) => {
    const families = data.map((row: any) => ({
      join_date: row['Join Date'] ? new Date(row['Join Date']).toISOString().split('T')[0] : null,
      status: row.Status || 'Active',
      last_name: row['Last Name(s)'] || '',
      first_name: row['First Name(s)'] || '',
      combined_name: row['Combined Id and Name'] || `${row['First Name(s)']} ${row['Last Name(s)']}`,
      address: row.Address || null,
      email: row['E-mail'] || '',
      phone_number: row['Phone Number'] || null
    }));

    setImportStatus(`Importing ${families.length} families to database...`);
    const { error } = await supabase.from('families').insert(families);
    if (error) {
      console.error('Supabase error:', error);
      throw new Error(error.message || 'Database error');
    }
  };

  const importAssignments = async (data: any[]) => {
    // First, get current students and families to match by names/IDs
    const [studentsResult, familiesResult] = await Promise.all([
      supabase.from('students').select('*'),
      supabase.from('families').select('*')
    ]);

    const students = studentsResult.data || [];
    const families = familiesResult.data || [];

    const assignments = data.map((row: any) => {
      // Try to find student by ID first, then by name
      let student = students.find(s => s.student_id && s.student_id.toString() === row['Student Id']?.toString());
      if (!student) {
        student = students.find(s => 
          s.combined_name === row['Student Name'] ||
          `${s.first_name} ${s.family_name}` === row['Student Name']
        );
      }

      // Try to find family by ID first, then by name
      let family = families.find(f => f.id.toString() === row['Family Id']?.toString());
      if (!family) {
        family = families.find(f => 
          f.combined_name === row['Family Name'] ||
          f.last_name === row['Family Name']
        );
      }

      if (!student || !family) {
        console.warn('Could not match assignment:', row);
        return null;
      }

      return {
        family_id: family.id,
        student_id: student.id,
        semester: row.Semester || 'Spring 2025',
        family_email_sent_at: (row['Email Sent to Family'] === 'TRUE' || row['Email Sent to Family'] === true) ? new Date().toISOString() : null,
        student_email_sent_at: (row['Email Sent to student'] === 'TRUE' || row['Email Sent to student'] === true) ? new Date().toISOString() : null
      };
    }).filter(Boolean); // Remove null entries

    setImportStatus(`Importing ${assignments.length} assignments to database...`);
    const { error } = await supabase.from('assignments').insert(assignments);
    if (error) {
      console.error('Supabase error:', error);
      throw new Error(error.message || 'Database error');
    }
  };

  return (
    <div className="csv-import">
      <h3>Import {type === 'students' ? 'Students' : type === 'families' ? 'Families' : 'Assignments'} from CSV</h3>
      <input 
        type="file" 
        accept=".csv" 
        onChange={handleFileUpload}
        disabled={isImporting}
      />
      {isImporting && <p>Importing...</p>}
      {importStatus && <p>{importStatus}</p>}
    </div>
  );
};